import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight, Package, Clock, CheckCircle2, Truck, XCircle, FileText } from 'lucide-react'
import { formatVND } from '@/lib/formatters'
import { useMyOrders, type OrderStatus } from '@/modules/orders-api'
import { useApiAuth } from '@/modules/auth-api'

const STATUS_LABEL: Record<OrderStatus, { label: string; cls: string; icon: React.ComponentType<{ className?: string }> }> = {
  PENDING:   { label: 'Chờ xác nhận', cls: 'bg-amber-100 text-amber-700',    icon: Clock },
  CONFIRMED: { label: 'Đã xác nhận',  cls: 'bg-blue-100 text-blue-700',      icon: CheckCircle2 },
  SHIPPING:  { label: 'Đang giao',    cls: 'bg-indigo-100 text-indigo-700',  icon: Truck },
  DELIVERED: { label: 'Hoàn tất',     cls: 'bg-accent-500/15 text-accent-700', icon: Package },
  CANCELLED: { label: 'Đã hủy',       cls: 'bg-red-100 text-red-700',        icon: XCircle },
}

export function MyOrdersPage() {
  const isAuthed = useApiAuth((s) => s.isAuthed)
  const [status, setStatus] = useState<OrderStatus | ''>('')
  const { data, isLoading } = useMyOrders({ page: 1, pageSize: 20, status: status || undefined })

  if (!isAuthed) {
    return (
      <div className="max-w-[720px] mx-auto px-6 py-16 text-center">
        <h1 className="text-2xl font-bold text-text">Vui lòng đăng nhập</h1>
        <p className="text-muted mt-2">Đăng nhập để xem lịch sử đơn hàng của bạn.</p>
        <Link to="/dang-nhap" className="mt-5 inline-flex h-11 px-6 rounded-md bg-brand-600 hover:bg-brand-700 text-white font-semibold text-sm items-center gap-2">
          Đăng nhập
        </Link>
      </div>
    )
  }

  const orders = data?.items ?? []

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-6">
      <nav className="flex items-center gap-1.5 text-sm text-muted mb-4">
        <Link to="/" className="hover:text-brand-600">Trang chủ</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-text font-medium">Đơn hàng của tôi</span>
      </nav>

      <h1 className="text-2xl font-bold text-text mb-5">Đơn hàng của tôi</h1>

      <div className="flex gap-2 flex-wrap mb-5">
        {[
          ['', 'Tất cả'],
          ['PENDING', 'Chờ xác nhận'],
          ['CONFIRMED', 'Đã xác nhận'],
          ['SHIPPING', 'Đang giao'],
          ['DELIVERED', 'Hoàn tất'],
          ['CANCELLED', 'Đã hủy'],
        ].map(([val, label]) => (
          <button
            key={val || 'all'}
            onClick={() => setStatus(val as OrderStatus | '')}
            className={[
              'h-9 px-4 rounded-full text-sm font-medium border transition',
              status === val
                ? 'bg-brand-600 text-white border-brand-600'
                : 'bg-surface text-text border-border hover:border-brand-500',
            ].join(' ')}
          >
            {label}
          </button>
        ))}
      </div>

      {isLoading && <div className="text-center py-16 text-muted">Đang tải...</div>}

      {!isLoading && orders.length === 0 && (
        <div className="bg-surface border border-border rounded-xl p-12 text-center">
          <Package className="w-14 h-14 mx-auto text-slate-300 mb-3" />
          <div className="text-text font-semibold">Chưa có đơn hàng nào</div>
          <p className="text-muted text-sm mt-1">Đơn hàng bạn đặt sẽ hiện tại đây.</p>
          <Link to="/" className="mt-4 inline-flex h-10 px-5 rounded-md bg-brand-600 hover:bg-brand-700 text-white font-medium text-sm items-center gap-2">
            Mua sắm ngay
          </Link>
        </div>
      )}

      <div className="space-y-4">
        {orders.map((o) => {
          const S = STATUS_LABEL[o.status]
          const Icon = S.icon
          return (
            <div key={o.id} className="bg-surface border border-border rounded-xl shadow-card overflow-hidden">
              <div className="px-5 h-14 border-b border-border flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-muted" />
                  <span className="font-semibold text-text">{o.code ?? o.id}</span>
                  <span className="text-xs text-muted">{new Date(o.createdAt).toLocaleString('vi-VN')}</span>
                </div>
                <span className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-xs font-semibold ${S.cls}`}>
                  <Icon className="w-3.5 h-3.5" /> {S.label}
                </span>
              </div>

              <div className="p-5 space-y-3">
                {o.items.slice(0, 3).map((it) => (
                  <div key={it.id} className="flex gap-3">
                    <div className="w-12 h-12 shrink-0 rounded-md overflow-hidden bg-slate-100 border border-border">
                      <img
                        src={it.image}
                        alt={it.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><rect width="1" height="1" fill="%23e2e8f0"/></svg>'
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-text line-clamp-1">{it.name}</div>
                      <div className="text-xs text-muted">SL: {it.qty} · {formatVND(it.price)}</div>
                    </div>
                  </div>
                ))}
                {o.items.length > 3 && (
                  <div className="text-xs text-muted">...và {o.items.length - 3} sản phẩm khác</div>
                )}
              </div>

              <div className="px-5 h-14 border-t border-border bg-slate-50 flex items-center justify-between">
                <span className="text-sm text-muted">
                  {o.items.length} sản phẩm · {o.payment === 'COD' ? 'COD' : 'QR'}
                </span>
                <div className="flex items-center gap-3">
                  <span className="text-brand-700 font-bold">{formatVND(o.grandTotal)}</span>
                  <Link
                    to={`/dat-hang-thanh-cong?id=${o.id}`}
                    className="h-9 px-4 rounded-md border border-border hover:border-brand-500 hover:text-brand-600 text-sm font-medium transition inline-flex items-center"
                  >
                    Chi tiết
                  </Link>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
